import type { QueryKey } from '@tanstack/react-query';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import type { HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';
import { emit } from '../events.js';
import { hashViewerScope } from './viewer-overlay.js';

export interface BackerRewardDiagnostic {
  readonly kind:
    | 'scope-mismatch'
    | 'stale-event'
    | 'missing-entity-recovery-failure'
    | 'backing-reconciliation-failure'
    | 'claim-reconciliation-failure';
  readonly entityHash: string;
  readonly reason?: string;
}

/** Stable, order-independent identity + wallet-set discriminator. It remains
 * an SDK-only hook arg; only its hash is written into a query key. */
export function createBackerRewardScope(
  userId: string | null | undefined,
  walletPubkeys: readonly string[],
): string {
  const wallets = [...new Set(walletPubkeys.filter((wallet) => wallet.length > 0))].sort();
  return `${userId?.trim() || 'anon'}\u0000${wallets.join('\u0000')}`;
}

/** Resolve a manifest-declared cross-route entity key for the active viewer.
 * The provider scope protects auth/impersonation/logout boundaries; the
 * manifest arg protects identity + wallet-set changes inside that viewer. */
export function resolveEntityQueryKey<Args extends JsonSerialisableArgs, T>(
  spec: HookSpec<Args, T>,
  args: Args,
  viewerScope: string,
): ((id: string) => QueryKey) | undefined {
  if (spec.entityStore === undefined) return spec.entityQueryKey;
  const rawScope =
    spec.entityStore.scopeArg === undefined
      ? undefined
      : (args as Record<string, unknown>)[spec.entityStore.scopeArg];
  const validScope = typeof rawScope === 'string' && rawScope.length > 0;
  if (spec.entityStore.scopeArg !== undefined && !validScope) {
    recordBackerRewardDiagnostic({
      kind: 'scope-mismatch',
      entityHash: hashDiagnosticEntity(spec.specName),
      reason: 'missing-scope-arg',
    });
  }
  const scopeHash = hashViewerScope(
    spec.entityStore.scopeArg === undefined
      ? viewerScope
      : `${viewerScope}\u0000${validScope ? rawScope : `missing:${spec.specName}`}`,
  );
  return (id: string): QueryKey => [
    spec.entityStore?.resource ?? 'entities',
    'viewer',
    scopeHash,
    id,
  ];
}

export function recordBackerRewardDiagnostic(diagnostic: BackerRewardDiagnostic): void {
  if (process.env.NODE_ENV !== 'production') {
    if (diagnostic.kind === 'scope-mismatch') _bumpPerf('backerReward.scopeMismatches');
    if (diagnostic.kind === 'stale-event') _bumpPerf('backerReward.staleEventsRejected');
    if (diagnostic.kind === 'missing-entity-recovery-failure') {
      _bumpPerf('backerReward.missingEntityRecoveryFailures');
    }
    if (diagnostic.kind === 'claim-reconciliation-failure') {
      _bumpPerf('backerReward.claimReconciliationFailures');
    }
    if (diagnostic.kind === 'backing-reconciliation-failure') {
      _bumpPerf('backerReward.backingReconciliationFailures');
    }
  }
  emit('backer-reward:diagnostic', diagnostic);
}
